// =============================================================================
// CR099: MERCHANT KEY UTILITIES
// =============================================================================
// Deterministic key-based comparison for promo_merchant vs reward_brand
// Avoids flaky string comparisons (case, punctuation, "Canada" suffix, etc.)
// =============================================================================

import { normalizeBrandName, normalizeMerchant } from "@/lib/brands/brandNormalization";

/**
 * Normalize a merchant/brand name to a deterministic key for comparison.
 * This key is used for "distinctness" checks between promo_merchant and reward_brand.
 * 
 * Process:
 * 1. Lowercase
 * 2. Remove punctuation except hyphens
 * 3. Collapse whitespace
 * 4. Remove common suffixes (inc, canada, ca, gift card, etc.)
 * 5. Convert spaces to hyphens
 */
export function toMerchantKey(name: string | null | undefined): string | null {
  if (!name || name.trim() === "") return null;
  
  return name
    .toLowerCase()
    .trim()
    // Normalize apostrophes and quotes
    .replace(/[''""]/g, "")
    // Remove common suffixes
    .replace(/\b(inc|incorporated|ltd|limited|canada|ca|gift\s*card|e?gift|card|store)\b/gi, "")
    // Remove punctuation except hyphens
    .replace(/[^\w\s-]/g, " ")
    // Collapse whitespace
    .replace(/\s+/g, " ")
    .trim()
    // Convert spaces to hyphens
    .replace(/\s/g, "-")
    // Remove consecutive hyphens
    .replace(/-+/g, "-")
    // Remove leading/trailing hyphens
    .replace(/^-|-$/g, "");
}

/**
 * Check if two merchant/brand names are "the same" entity.
 * Uses key-based comparison for determinism.
 * 
 * Returns true if:
 * - Both keys are equal after normalization
 * - One is a substring of the other (handles "Walmart" vs "Walmart Canada")
 * 
 * @param a First merchant/brand name (or key)
 * @param b Second merchant/brand name (or key)
 */
export function areSameMerchant(
  a: string | null | undefined, 
  b: string | null | undefined
): boolean {
  const keyA = toMerchantKey(a);
  const keyB = toMerchantKey(b);
  
  // If either is null/empty, they're not "the same"
  if (!keyA || !keyB) return false;
  
  // Exact match
  if (keyA === keyB) return true;
  
  // Substring match (handles "walmart" vs "walmart-travel" or "walmart-canada")
  // Only match if one is a prefix of the other with hyphen boundary
  if (keyA.startsWith(keyB + "-") || keyB.startsWith(keyA + "-")) {
    return true;
  }
  
  return false;
}

/**
 * Check if two merchants/brands are "distinct" (different entities).
 * This is the inverse of areSameMerchant.
 * 
 * @param a First merchant/brand name (or key)
 * @param b Second merchant/brand name (or key)
 */
export function areDistinctMerchants(
  a: string | null | undefined, 
  b: string | null | undefined
): boolean {
  const keyA = toMerchantKey(a);
  const keyB = toMerchantKey(b);
  
  // If either is null/empty, consider them distinct (can't compare)
  if (!keyA || !keyB) return true;
  
  return !areSameMerchant(a, b);
}

/**
 * Given promo_merchant and reward_brand info, determine if reward chip should show.
 * Uses key-based comparison when keys are available, falls back to name comparison.
 * 
 * @param promoKey promo_merchant_key (normalized key)
 * @param promoName promo_merchant_name (display name)
 * @param rewardKey reward_brand_key (normalized key)
 * @param rewardName reward_brand_name (display name)
 */
export function shouldShowRewardChip(
  promoKey: string | null | undefined,
  promoName: string | null | undefined,
  rewardKey: string | null | undefined,
  rewardName: string | null | undefined
): boolean {
  // No reward brand at all → no chip
  if (!rewardName && !rewardKey) return false;
  
  // If we have both keys, use key comparison (most reliable)
  if (promoKey && rewardKey) {
    return promoKey !== rewardKey;
  }
  
  // Fall back to name-based comparison
  return areDistinctMerchants(promoName, rewardName);
}

// =============================================================================
// VALIDATION FLAG TYPES (CR099 point 4)
// =============================================================================

export type ValidationFlagSeverity = "BLOCK" | "WARN";

export interface ValidationFlagDefinition {
  flag: string;
  severity: ValidationFlagSeverity;
  description: string;
}

/**
 * CR099: Validation flag definitions with severity levels.
 * BLOCK = publish must fail
 * WARN = publish allowed but surfaced in admin
 */
export const CR099_VALIDATION_FLAGS: ValidationFlagDefinition[] = [
  // BLOCK flags - publish must fail
  { flag: "MISSING_PROMO_MERCHANT", severity: "BLOCK", description: "promo_merchant_name is required for all deals" },
  { flag: "MISSING_REWARD_BRAND_FOR_GIFTCARD", severity: "BLOCK", description: "reward_brand_name is required when reward_type is gift_card" },
  
  // WARN flags - publish allowed but visible in admin
  { flag: "PROMO_MERCHANT_MISMATCH_WARNING", severity: "WARN", description: "Possible mismatch: gaming brand sold at big-box retailer" },
  { flag: "PROMO_REWARD_SIMILAR_NOT_EQUAL", severity: "WARN", description: "promo_merchant and reward_brand are similar but not equal (possible normalization issue)" },
  { flag: "REWARD_TYPE_AMBIGUOUS", severity: "WARN", description: "reward_type was inferred but confidence is low" },
  { flag: "QUALIFYING_PRODUCT_MISSING", severity: "WARN", description: "qualifying_product_name is missing (optional but recommended)" },
];

/**
 * Get the severity of a validation flag.
 * Unknown flags default to WARN.
 */
export function getFlagSeverity(flag: string): ValidationFlagSeverity {
  const def = CR099_VALIDATION_FLAGS.find(f => f.flag === flag);
  return def?.severity ?? "WARN";
}

/**
 * Partition validation flags into blocking and warning.
 */
export function partitionValidationFlags(flags: string[]): {
  blocking: string[];
  warnings: string[];
} {
  const blocking: string[] = [];
  const warnings: string[] = [];
  
  for (const flag of flags) {
    if (getFlagSeverity(flag) === "BLOCK") {
      blocking.push(flag);
    } else {
      warnings.push(flag);
    }
  }
  
  return { blocking, warnings };
}
